import React from "react";
import { Image as ImageIcon } from "lucide-react";
import { WIZARD_TEMPLATES, SLA_BY_SEV, PARTY_LAYER_COLOR } from "../../data/wizardTemplates";
import { severityColor } from "../../data/mockData";
import { SectionLabel } from "./wizardAtoms";

export const WizardStep4Review = ({ form, busy, progress, onSubmit, onSubmitDraft }) => {
  const tpl = WIZARD_TEMPLATES.find((t) => t.value === form.template);
  const sla = SLA_BY_SEV[form.severity] || SLA_BY_SEV.s3;
  const sevC = severityColor(form.severity);

  return (
    <div data-testid="wizard-step4">
      <h2
        style={{
          fontFamily: "Syne, sans-serif",
          fontSize: 22,
          fontWeight: 600,
          color: "#f0f2f8",
          marginBottom: 24,
          letterSpacing: "-0.3px",
        }}
      >
        Review &amp; submit
      </h2>

      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
        <div
          className="rounded-md p-4"
          style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)" }}
          data-testid="wizard-review-incident"
        >
          <SectionLabel>Incident</SectionLabel>
          <div className="flex items-center gap-2 mb-2">
            <span
              className="px-2 py-0.5 rounded"
              style={{
                background: `${sevC}22`,
                border: `1px solid ${sevC}66`,
                color: sevC,
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                letterSpacing: "1.2px",
                textTransform: "uppercase",
              }}
            >
              {sla.label}
            </span>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0" }}>
              {tpl?.label}
            </span>
          </div>
          <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 16, color: "#f0f2f8" }}>
            {form.title || "(no title)"}
          </div>
          <p
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              color: "#c5cad8",
              marginTop: 6,
              lineHeight: 1.55,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {form.description}
          </p>
          <div
            className="mt-3 grid grid-cols-2 gap-2"
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9198b0" }}
          >
            <div>Parts: <span style={{ color: "#9ec3ec" }}>{form.parts.length}</span></div>
            <div>Line: <span style={{ color: "#9ec3ec" }}>{form.line || "—"}</span></div>
            <div>EUR loss: <span style={{ color: "#9ec3ec" }}>€{form.loss || "0"}</span></div>
            <div>Units · h: <span style={{ color: "#9ec3ec" }}>{form.units || 0} · {form.hours || 0}</span></div>
          </div>
        </div>

        <div
          className="rounded-md p-4"
          style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)" }}
          data-testid="wizard-review-parties"
        >
          <SectionLabel>Parties · {(tpl?.parties.length || 0) + (form.additionalParties?.length || 0)} total</SectionLabel>
          <div className="space-y-1.5" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>
            {tpl?.parties.map((p) => (
              <div key={p} className="flex items-center gap-2">
                <span
                  className="inline-block rounded-full"
                  style={{ width: 6, height: 6, background: PARTY_LAYER_COLOR[p] || "#9198b0" }}
                />
                <span style={{ color: "#f0f2f8" }}>{p}</span>
                <span style={{ color: "#6b7388" }}>· Required</span>
              </div>
            ))}
            {(form.additionalParties || []).map((p) => (
              <div key={p.id} className="flex items-center gap-2">
                <span
                  className="inline-block rounded-full"
                  style={{ width: 6, height: 6, background: PARTY_LAYER_COLOR[p.type] || "#9ec3ec" }}
                />
                <span style={{ color: "#f0f2f8" }}>{p.name}</span>
                <span style={{ color: "#6b7388" }}>· {p.role}</span>
              </div>
            ))}
          </div>
          <div
            className="mt-3"
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0" }}
          >
            Notifications: <span style={{ color: "#4ed084" }}>Email</span> ·{" "}
            {form.severity === "s1" ? <span style={{ color: "#4ed084" }}>SMS</span> : <span style={{ color: "#6b7388" }}>SMS (skipped)</span>}{" "}
            · <span style={{ color: "#4ed084" }}>In-app</span>
          </div>
        </div>

        <div
          className={form.severity === "s1" ? "sla-card-s1" : ""}
          style={{
            background: "#0d0f18",
            border: `1px solid ${form.severity === "s1" ? "rgba(226,75,74,0.55)" : "rgba(255,255,255,0.10)"}`,
            borderRadius: 6,
            padding: 16,
          }}
          data-testid="wizard-review-sla"
        >
          <div className="flex items-center justify-between mb-2">
            <SectionLabel>SLA Commitment</SectionLabel>
            {form.severity === "s1" && (
              <span
                className="live-dot inline-block rounded-full"
                style={{ width: 8, height: 8, background: "#E24B4A" }}
              />
            )}
          </div>
          <div
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8", lineHeight: 1.7 }}
          >
            <div>
              Acknowledgement required within <span style={{ color: sevC, fontWeight: 600 }}>{sla.ack}</span>
            </div>
            <div>
              Resolution required within <span style={{ color: sevC, fontWeight: 600 }}>{sla.resolve}</span>
            </div>
            <div style={{ color: "#9198b0" }}>
              Auto-escalation fires at <span style={{ color: "#f3b14a" }}>75% of resolution SLA</span>
            </div>
          </div>
        </div>

        <div
          className="rounded-md p-4"
          style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)" }}
          data-testid="wizard-review-attachments"
        >
          <SectionLabel>Attachments · {form.files.length}</SectionLabel>
          {form.files.length === 0 && (
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#6b7388" }}>
              No attachments
            </div>
          )}
          <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))" }}>
            {form.files.map((f, i) => {
              const isImg = f.type && f.type.startsWith("image/");
              const url = isImg ? URL.createObjectURL(f) : null;
              return (
                <div
                  key={i}
                  className="rounded overflow-hidden"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  {isImg ? (
                    <img src={url} alt={f.name} style={{ width: "100%", height: 60, objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: "100%", height: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <ImageIcon size={18} color="#9ec3ec" />
                    </div>
                  )}
                  <div
                    className="px-1 py-0.5"
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 9,
                      color: "#c5cad8",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {f.name}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy}
          className="transition-all"
          style={{
            background: busy ? "#0c4076" : "#185FA5",
            border: "1px solid #185FA5",
            color: "#fff",
            fontFamily: "Syne, sans-serif",
            fontWeight: 600,
            fontSize: 14,
            letterSpacing: "0.5px",
            padding: "10px 22px",
            borderRadius: 6,
            cursor: busy ? "wait" : "pointer",
            opacity: busy ? 0.85 : 1,
          }}
          data-testid="wizard-submit-btn"
        >
          {busy ? "Submitting…" : "Open Incident →"}
        </button>
        <button
          type="button"
          onClick={onSubmitDraft}
          disabled={busy}
          className="transition-colors"
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "#c5cad8",
            fontFamily: "Syne, sans-serif",
            fontWeight: 600,
            fontSize: 13,
            padding: "10px 18px",
            borderRadius: 6,
            cursor: busy ? "wait" : "pointer",
          }}
          data-testid="wizard-draft-btn"
        >
          Submit as Draft
        </button>
        <div
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 10,
            color: "#9198b0",
            letterSpacing: "0.3px",
            marginLeft: 4,
            lineHeight: 1.5,
          }}
        >
          By submitting, all linked parties are notified and the SLA timer starts.
        </div>
      </div>

      {progress && (
        <div
          className="mt-4 p-3 rounded-md"
          style={{ background: "rgba(24,95,165,0.08)", border: "1px solid rgba(24,95,165,0.30)" }}
          data-testid="wizard-progress"
        >
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              color: "#9ec3ec",
              letterSpacing: "0.3px",
            }}
          >
            ▸ {progress}
          </div>
        </div>
      )}
    </div>
  );
};
